import React, { useContext, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../features/auth/authContext.js';
import { TelegramContext } from '../features/telegram/telegramContext.js';
import { AppleSignInButton } from '../components/AppleSignInButton.js';
import { formatAppleAuthError } from '../features/auth/appleAuth.js';

function formatRegisterError(err: unknown, fallback: string): string {
  if (typeof err === 'object' && err !== null && 'code' in err) {
    const code = String((err as { code: unknown }).code);
    if (code === 'auth/email-already-in-use') {
      return 'An account with this email address already exists. Please sign in instead.';
    }
    if (code === 'auth/invalid-email') return 'Please enter a valid email address.';
    if (code === 'auth/weak-password') {
      return 'Password is too weak. Please use at least 8 characters with numbers and letters.';
    }
    if (code === 'auth/popup-closed-by-user')
      return 'Google sign-up window was closed before completing.';
    if (code === 'auth/popup-blocked')
      return 'Sign-up pop-up was blocked by browser. Please enable pop-ups.';
    if (code === 'auth/network-request-failed')
      return 'Network error. Please check your internet connection and try again.';
  }
  return fallback;
}

export const RegisterPage: React.FC = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const telegramContext = useContext(TelegramContext);
  const isTelegram = telegramContext?.environment.isTelegram ?? false;

  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError('Use a password with at least 8 characters.');
      return;
    }
    setSubmitting(true);
    try {
      await auth.signUpWithEmail(displayName, email, password);
      navigate('/verify-email', { replace: true });
    } catch (err: unknown) {
      setError(
        formatRegisterError(err, 'Unable to create your account. Verify the details and try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleSignIn = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await auth.signInWithGoogle();
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(formatRegisterError(err, 'Unable to sign in with Google. Please try again.'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleAppleSignIn = async (): Promise<void> => {
    setError(null);
    setSubmitting(true);
    try {
      await auth.signInWithApple();
      navigate('/dashboard', { replace: true });
    } catch (err: unknown) {
      setError(formatAppleAuthError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-form-page">
      <div className="auth-form-heading">
        <h2>Create PAY888 Account</h2>
        <p>No ID upload needed to create your PAY888 account.</p>
        <small>Card and financial-service identity checks may vary by provider.</small>
      </div>
      {!auth.isConfigured && (
        <p className="auth-form-error" role="alert">
          Firebase Authentication is not configured for this environment.
        </p>
      )}
      {error && (
        <p className="auth-form-error" role="alert">
          {error}
        </p>
      )}
      <form className="auth-form" onSubmit={(event) => void submit(event)}>
        <label>
          Full Name
          <input
            type="text"
            autoComplete="name"
            value={displayName}
            onChange={(event) => setDisplayName(event.target.value)}
            required
            placeholder="Alex Morgan"
          />
        </label>
        <label>
          Email Address
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            required
            placeholder="you@example.com"
          />
        </label>
        <label>
          Password
          <input
            type="password"
            autoComplete="new-password"
            minLength={8}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
            required
            placeholder="Min. 8 characters"
          />
        </label>
        <button
          type="submit"
          className="ios-btn-primary"
          disabled={!auth.isConfigured || submitting}
          style={{ width: '100%', marginTop: '4px' }}
        >
          {submitting ? 'Creating Account…' : 'Create Account'}
        </button>
      </form>
      {!isTelegram && (
        <div className="auth-social-buttons">
          <button
            type="button"
            className="auth-google-button ios-btn-secondary"
            onClick={() => void handleGoogleSignIn()}
            disabled={!auth.isConfigured || submitting}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            <span>Continue with Google</span>
          </button>
          <AppleSignInButton
            disabled={!auth.isConfigured || submitting}
            onClick={() => void handleAppleSignIn()}
          />
        </div>
      )}
      <div className="auth-form-links auth-form-links-centered">
        Already have an account? <Link to="/login">Sign in</Link>
      </div>
    </div>
  );
};
